import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Footer from "./Footer";
import Logo from "./Logo"; // Integrated your new Logo component
import {
  LayoutDashboard,
  Workflow,
  History,
  BarChart3,
  CreditCard,
  Shield,
  LogOut,
  Instagram,
  Globe,
  Settings,
} from "lucide-react";

const links = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/app/rules", label: "Automation Rules", icon: Workflow, testid: "nav-rules" },
  { to: "/app/triggers", label: "Trigger History", icon: History, testid: "nav-triggers" },
  { to: "/app/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics" },
  { to: "/app/platforms", label: "Platforms", icon: Globe, testid: "nav-platforms" },
  { to: "/app/billing", label: "Billing", icon: CreditCard, testid: "nav-billing" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const onLogout = async () => {
    try {
      await logout();
      nav("/");
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  return (
    <div className="min-h-screen bg-background grid-noise">
      <div className="flex">
        {/* Sidebar (Desktop) */}
        <aside className="hidden md:flex w-64 min-h-screen flex-col border-r-2 border-foreground bg-white sticky top-0">
          <div className="p-6 border-b-2 border-foreground bg-[#fdfcf7]">
            {/* BRANDING UPDATED: Using Logo Component */}
            <Link to="/app" className="block transition-transform active:scale-95">
              <Logo />
            </Link>
          </div>

          <nav className="p-4 space-y-1 flex-1 overflow-y-auto">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md font-semibold text-sm border-2 transition-all ${
                    isActive
                      ? "bg-primary border-foreground shadow-brutal-sm"
                      : "border-transparent hover:bg-muted hover:border-foreground"
                  }`
                }
              >
                <l.icon className="w-4 h-4" strokeWidth={2.5} />
                {l.label}
              </NavLink>
            ))}

            {user?.role === "admin" && (
              <NavLink
                to="/admin"
                data-testid="nav-admin"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md font-semibold text-sm border-2 transition-all ${
                    isActive 
                      ? "bg-accent border-foreground shadow-brutal-sm" 
                      : "border-transparent hover:bg-muted hover:border-foreground"
                  }`
                }
              >
                <Shield className="w-4 h-4" strokeWidth={2.5} />
                Admin Panel
              </NavLink>
            )}
          </nav>

          <div className="p-4 border-t-2 border-foreground space-y-2 bg-white">
            <div className="flex items-center gap-2 px-2 py-2">
              <div className="w-8 h-8 rounded-full bg-secondary border-2 border-foreground flex items-center justify-center font-extrabold text-sm shrink-0">
                {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate" data-testid="user-name">
                  {user?.name || "User"}
                </div>
                <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
              </div>
            </div>
            <button
              onClick={onLogout}
              data-testid="logout-btn"
              className="w-full flex items-center justify-center gap-2 px-3 py-2 border-2 border-foreground rounded-md text-sm font-bold hover:bg-destructive hover:text-white transition-colors"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </aside>

        {/* Mobile Top Bar */}
        <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b-2 border-foreground p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* BRANDING UPDATED: Using Logo Component for Mobile */}
            <Logo />
          </div>
          <button 
            onClick={onLogout} 
            aria-label="Logout"
            className="p-2 border-2 border-foreground rounded-md text-sm font-bold active:bg-muted transition-colors"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>

        {/* Main Content Area */}
        <main className="flex-1 min-w-0 pt-16 pb-24 md:pt-0 md:pb-0 flex flex-col min-h-screen">
          <div className="p-4 md:p-8 lg:p-12 max-w-7xl flex-1 w-full mx-auto">
            <Outlet />
          </div>
          <Footer />
        </main>

        {/* Mobile Bottom Nav */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t-2 border-foreground flex justify-around items-center p-1 pb-safe">
          {links.slice(0, 5).map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`m-${l.testid}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 p-2 flex-1 text-center transition-colors ${
                  isActive ? "text-primary-foreground bg-primary/10 rounded-lg" : "text-muted-foreground"
                }`
              }
            >
              <l.icon className={`w-5 h-5 ${l.end ? "" : ""}`} strokeWidth={2.5} />
              <span className="text-[10px] font-bold leading-none truncate w-full">
                {l.label.split(" ")[0]}
              </span>
            </NavLink>
          ))}
          <NavLink
            to="/app/billing"
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 p-2 flex-1 text-center ${
                isActive ? "text-primary" : "text-muted-foreground"
              }`
            }
          >
            <CreditCard className="w-5 h-5" strokeWidth={2.5} />
            <span className="text-[10px] font-bold leading-none">Billing</span>
          </NavLink>
        </nav>
      </div>
    </div>
  );
}

/**
 * Reusable Status Badge Component with error tooltip
 */
export function StatusBadge({ status, errorMessage }) {
  const styles = {
    sent: "bg-mint text-foreground",
    failed: "bg-destructive text-destructive-foreground",
    blocked_by_meta: "bg-rose-100 text-rose-900 border border-rose-200",
    blocked_quota: "bg-butter text-foreground",
    duplicate: "bg-muted text-muted-foreground",
    queued: "bg-sky text-foreground",
    prepared: "bg-sky text-foreground",
    triggered: "bg-slate-200 text-slate-900",
  };
  
  const labels = {
    sent: "Sent",
    failed: "Failed",
    blocked_by_meta: "Blocked by Meta",
    blocked_quota: "Quota Hit",
    duplicate: "Duplicate",
    queued: "Prepared",
    prepared: "Prepared",
    triggered: "Triggered",
  };

  const isMetaBlock = status === "blocked_by_meta";
  const hasError = status === "failed" && errorMessage;
  const advancedAccessRequired = isMetaBlock || (errorMessage || "").includes("Advanced Access");

  if (isMetaBlock) {
    return (
      <div className="brutal rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">
        <div className="flex items-center gap-2 font-semibold mb-2">Meta Capability Pending</div>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span>✅</span>
            <span>Trigger detected</span>
          </div>
          <div className="flex items-center gap-2">
            <span>✅</span>
            <span>DM request prepared</span>
          </div>
          <div className="flex items-center gap-2">
            <span>❌</span>
            <span>Blocked by Meta approval</span>
          </div>
        </div>
        {advancedAccessRequired && (
          <div className="mt-3 rounded-md bg-rose-100 p-2 text-xs text-rose-800 border border-rose-200">
            Instagram Messaging Advanced Access required
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={hasError ? "relative group cursor-help" : ""}>
      <span className={`chip px-2 py-0.5 rounded text-xs font-bold border border-foreground ${styles[status] || "bg-muted"}`}>
        {labels[status] || status}
      </span>
      {hasError && (
        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block bg-foreground text-background rounded px-2 py-1 text-xs whitespace-nowrap z-50 shadow-lg">
          <div className="max-w-xs">
            {advancedAccessRequired ? (
              <div>
                <p className="font-bold">Advanced Access Required</p>
                <p className="text-[10px] mt-1">See INSTAGRAM_MESSAGING_SETUP.md for instructions</p>
              </div>
            ) : (
              <p>{errorMessage.substring(0, 100)}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Reusable Instagram Pill Component
 */
export function IGPill() {
  return (
    <span className="inline-flex items-center gap-1.5 chip bg-secondary px-2 py-0.5 rounded text-xs font-bold border border-foreground">
      <Instagram className="w-3 h-3" strokeWidth={2.5} /> Instagram
    </span>
  );
}